import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Plane } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../data/translations';
import { Button } from './ui/button';

const Header: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { language, switchToLanguage } = useLanguage();
  const location = useLocation();
  const t = translations[language];

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-2 group">
            <div className="bg-brand-navy p-2 rounded-lg group-hover:bg-brand-blue transition-colors duration-300">
              <Plane className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold text-brand-navy">Delay24</span>
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            {[
              { to: '/', label: t.nav.home },
              { to: '/how-it-works', label: t.nav.howItWorks },
              { to: '/faq', label: t.nav.faq },
              { to: '/about', label: t.nav.about },
              { to: '/contact', label: t.nav.contact },
            ].map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={`text-sm font-medium transition-colors duration-200 ${
                  isActive(to) ? 'text-brand-blue' : 'text-gray-700 hover:text-brand-blue'
                }`}
              >
                {label}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center space-x-4">
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => switchToLanguage('tr')}
                className={`px-3 py-1 text-sm font-medium rounded transition-all duration-200 ${
                  language === 'tr' ? 'bg-brand-navy text-white' : 'text-gray-600 hover:text-brand-navy'
                }`}
              >
                TR
              </button>
              <button
                onClick={() => switchToLanguage('en')}
                className={`px-3 py-1 text-sm font-medium rounded transition-all duration-200 ${
                  language === 'en' ? 'bg-brand-navy text-white' : 'text-gray-600 hover:text-brand-navy'
                }`}
              >
                EN
              </button>
            </div>

            <Link to="/claim">
              <Button className="bg-brand-yellow hover:bg-brand-yellow-light text-brand-navy font-semibold shadow-md hover:shadow-lg transition-all duration-300">
                {t.nav.claim}
              </Button>
            </Link>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6 text-brand-navy" />
            ) : (
              <Menu className="h-6 w-6 text-brand-navy" />
            )}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col space-y-4">
              {[
                { to: '/', label: t.nav.home },
                { to: '/how-it-works', label: t.nav.howItWorks },
                { to: '/faq', label: t.nav.faq },
                { to: '/about', label: t.nav.about },
                { to: '/contact', label: t.nav.contact },
              ].map(({ to, label }) => (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`text-sm font-medium transition-colors duration-200 ${
                    isActive(to) ? 'text-brand-blue' : 'text-gray-700'
                  }`}
                >
                  {label}
                </Link>
              ))}

              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center bg-gray-100 rounded-lg p-1 mb-4 w-fit">
                  <button
                    onClick={() => switchToLanguage('tr')}
                    className={`px-4 py-2 text-sm font-medium rounded transition-all duration-200 ${
                      language === 'tr' ? 'bg-brand-navy text-white' : 'text-gray-600'
                    }`}
                  >
                    TR
                  </button>
                  <button
                    onClick={() => switchToLanguage('en')}
                    className={`px-4 py-2 text-sm font-medium rounded transition-all duration-200 ${
                      language === 'en' ? 'bg-brand-navy text-white' : 'text-gray-600'
                    }`}
                  >
                    EN
                  </button>
                </div>

                <Link to="/claim" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-brand-yellow hover:bg-brand-yellow-light text-brand-navy font-semibold">
                    {t.nav.claim}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
